import { Box, Stack, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import { getConversationsPaginatedList as getConversationsPaginatedListAction } from '../../../../actions/aiAssistant.actions';
import { customSizes, palette, spaces } from '../../../../assets/styles/themes/tokens';
import { AiLoadingStatus } from '../../../../reducers/aiAssistant.reducers';
import {
	getConversationsListPagination,
	getConversationsListResults,
	getConversationsLoadingState,
} from '../../../../reducers/aiAssistant.selectors';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import useAction from '../../../../utils/useAction';
import useRedirectIfNoAIAssistantAccess from '../../../../utils/useRedirectIfNoAIAssistantAccess';
import useScreenSize from '../../../../utils/useScreenSize';
import AIAssistantChatHistoryList from './AIAssistantChatHistoryList';
import AIAssistantChatHistoryLoader from './AIAssistantChatHistoryLoader';
import { hasNextConversationListPage } from './utils';

const getHistoryPageContainerStyles = (isSmallScreen: boolean) => ({
	maxWidth: isSmallScreen ? '100%' : customSizes.aiAssistantForm.containerMaxWidth,
	width: '100%',
	padding: isSmallScreen ? 0 : `${spaces.md} ${spaces.xxxl} ${spaces.xl} ${spaces.xxxl}`,
	alignItems: 'center',
	gap: spaces.lg,
	alignSelf: 'stretch',
	margin: '0 auto',
});

const AIAssistantChatHistory = () => {
	const { t } = useTranslation();
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const loadingState = useSelector(getConversationsLoadingState);
	const conversations = useSelector(getConversationsListResults);
	const pagination = useSelector(getConversationsListPagination);
	const { isLoading: isUserLoading, hasAccess } = useRedirectIfNoAIAssistantAccess();
	const getConversationsPaginatedList = useAction(getConversationsPaginatedListAction);
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isSmallScreen = isXsScreen || isSmScreen;
	useEffect(() => {
		if (loadingState === AiLoadingStatus.NONE) {
			getConversationsPaginatedList();
		}
	}, [loadingState, getConversationsPaginatedList]);

	const loadMore = () => {
		getConversationsPaginatedList();
	};

	const isLoading = [AiLoadingStatus.INITIAL, AiLoadingStatus.NONE].includes(loadingState);

	if (isUserLoading || !hasAccess) {
		return null;
	}

	return (
		<Box sx={getHistoryPageContainerStyles(isSmallScreen)}>
			{isLoading ? (
				<AIAssistantChatHistoryLoader />
			) : (
				<>
					{conversations.length > 0 ? (
						<InfiniteScroll
							loadMore={loadMore}
							hasMore={hasNextConversationListPage(pagination)}
							initialLoad={false}
							useWindow={false}
							getScrollParent={() => mainContentContainer || null}
						>
							<AIAssistantChatHistoryList items={conversations} />
						</InfiniteScroll>
					) : (
						<Stack spacing={spaces.xs} sx={{ padding: spaces.md }}>
							<Typography sx={{ color: palette.text.secondary }} variant="h6">
								{t("You haven't had any conversations yet")}
							</Typography>
							<Typography sx={{ color: palette.text.secondary }} variant="body2">
								{t('Once you start a chat, it will be displayed here')}
							</Typography>
						</Stack>
					)}
					{loadingState === AiLoadingStatus.PAGINATION && <AIAssistantChatHistoryLoader />}
				</>
			)}
		</Box>
	);
};

export default AIAssistantChatHistory;
